import React, { useEffect, useState } from "react";
import { api, formatRupiah, stageLabel, stageStyle } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { downloadCsv } from "../lib/download";

const empty = { name:"", company:"", contact_name:"", stage:"new", value:0, probability:10, product:"", expected_closing:"", notes:"" };

export default function Deals() {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const [deals, st] = await Promise.all([api.get("/deals"), api.get("/settings/stages")]);
    setItems(deals.data);
    setStages(st.data.sort((a,b)=>a.order-b.order));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/deals", { ...form, value: Number(form.value), probability: Number(form.probability) });
      toast.success("Deal ditambahkan");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Gagal menyimpan"); }
  };
  const del = async (id) => { if(!window.confirm("Hapus deal?"))return; await api.delete(`/deals/${id}`); load(); };
  const exportCsv = async () => {
    const ok = await downloadCsv("/export/deals.csv", `deals-${new Date().toISOString().slice(0,10)}.csv`);
    if (ok) toast.success("Ekspor deals berhasil"); else toast.error("Gagal ekspor");
  };

  const onDrop = async (stage) => {
    if (!dragId) return;
    const s = stages.find((x)=>x.key === stage);
    const prob = s ? s.probability : 20;
    try {
      await api.patch(`/deals/${dragId}/stage`, { stage, probability: prob });
      toast.success(`Dipindahkan ke ${stageLabel(stage, s?.name)}`);
      load();
    } catch { toast.error("Gagal memindahkan"); }
    setDragId(null);
  };

  const grandTotal = items.filter(d => !["won","lost"].includes(d.stage)).reduce((a,b)=>a+(b.value||0),0);
  const weighted = items.filter(d => !["won","lost"].includes(d.stage)).reduce((a,b)=>a+(b.value||0)*((b.probability||0)/100),0);

  return (
    <div className="space-y-5" data-testid="deals-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>Sales Pipeline</h1>
          <p className="text-sm text-slate-500">Total: <span className="font-semibold text-slate-800">{formatRupiah(grandTotal)}</span> · Weighted: <span className="font-semibold text-emerald-700">{formatRupiah(weighted)}</span></p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" onClick={exportCsv} data-testid="export-deals-btn"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="brand-btn" data-testid="add-deal-btn"><Plus className="w-4 h-4 mr-1"/>Deal Baru</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Deal Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><Label>Nama Deal</Label><Input data-testid="deal-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
              <div><Label>Perusahaan</Label><Input value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})}/></div>
              <div><Label>Kontak</Label><Input value={form.contact_name} onChange={(e)=>setForm({...form,contact_name:e.target.value})}/></div>
              <div><Label>Nilai (Rp)</Label><Input type="number" value={form.value} onChange={(e)=>setForm({...form,value:e.target.value})}/></div>
              <div><Label>Probabilitas (%)</Label><Input type="number" value={form.probability} onChange={(e)=>setForm({...form,probability:e.target.value})}/></div>
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v)=>{
                  const s = stages.find(x=>x.key===v);
                  setForm({...form, stage:v, probability: s ? s.probability : form.probability});
                }}>
                  <SelectTrigger data-testid="deal-stage"><SelectValue/></SelectTrigger>
                  <SelectContent>{stages.map(s=><SelectItem key={s.id} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Produk</Label><Input value={form.product} onChange={(e)=>setForm({...form,product:e.target.value})}/></div>
              <div className="col-span-2"><Label>Catatan</Label><Input value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button className="brand-btn" onClick={save} data-testid="deal-save">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-x-auto pb-4">
        {stages.map((col)=>{
          const dealsIn = items.filter((d) => d.stage === col.key);
          return (
            <div key={col.id}
              onDragOver={(e)=>e.preventDefault()}
              onDrop={()=>onDrop(col.key)}
              className="pipeline-col rounded-xl bg-slate-100/70 p-3 border border-slate-200"
              data-testid={`pipeline-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-700">{col.name}</span>
                <Badge variant="outline" className="text-xs">{dealsIn.length}</Badge>
              </div>
              <div className="space-y-2">
                {dealsIn.map((d)=>(
                  <div key={d.id} draggable
                    onDragStart={()=>setDragId(d.id)}
                    className="pipeline-card"
                    data-testid={`deal-card-${d.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="text-xs text-slate-500">{d.company}</div>
                      <button onClick={()=>del(d.id)} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                    <div className="text-sm font-medium text-slate-800 mt-1 line-clamp-2">{d.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-sm font-bold text-emerald-800">{formatRupiah(d.value)}</div>
                      <Badge variant="outline" className="text-[10px]">{d.probability}%</Badge>
                    </div>
                  </div>
                ))}
                {dealsIn.length === 0 && <div className="text-xs text-slate-400 text-center py-6">Kosong</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
